import type { UserResponse } from "@sidegigllc/api/client";
import type { QueryClient } from "@tanstack/react-query";
import { isOnsiteManager } from "domains/auth/access/roles";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { getFeatureFlag } from "providers/LaunchDarkly/client";

// Statically imported by the router, so it must not pull in the posting page's own dependencies.

const SWITCH_KEY = "qwick-posting-page-v2";
const switchStorage = createJSONStorage<Record<string, boolean>>(() => localStorage);

/**
 * The "Posting page V2" switch, by user id: kept on this browser through refreshes, new tabs and signing out and in. A
 * manager who turned it off is `false`; with no entry V2 is on, for everyone the opt-in targets.
 */
export const postingV2SwitchAtom = atomWithStorage<Record<string, boolean>>(SWITCH_KEY, {}, switchStorage, {
  getOnInit: true,
});

export const postingV2SwitchedOn = (switches: Record<string, boolean>, userId: number | undefined) =>
  userId !== undefined && switches[String(userId)] !== false;

/** The opt-in names who may switch; the posting page's own flag is its kill switch (our LaunchDarkly plan has no prerequisites). */
export const postingV2Available = (flags: { optIn: boolean; postingPage: boolean }) => flags.optIn && flags.postingPage;

type V2Gate = { optIn: boolean; postingPage: boolean; switchedOn: boolean; onsite: boolean };

/** Whether V2 is this manager's posting page: both flags, their switch not turned off, and a location manager (V2 is theirs only). */
export const postingV2Open = (gate: V2Gate) => postingV2Available(gate) && gate.switchedOn && !gate.onsite;

/**
 * Where `/post/shift`, which every "post a shift" entry opens, lands. The opt-in is R4's only switch: off, today's canvas
 * (the chat's flag) or form; on, no canvas, so V2 where it is open and today's form where it is not.
 */
export const postShiftDestination = (gate: V2Gate & { canvas: boolean }) => {
  if (!gate.optIn) return gate.canvas ? "canvas" : "form";
  return postingV2Open(gate) ? "v2" : "form";
};

/** The switch as this browser holds it now: storage, as the store follows another tab only while a page has the switch mounted. */
export const postingV2SwitchedOnNow = (userId: number | undefined) =>
  postingV2SwitchedOn(switchStorage.getItem(SWITCH_KEY, {}), userId);

/** Whether V2 opens in place of today's single-shift form: both flags, and this location manager's switch not turned off here. */
export const postingV2On = async (queryClient: QueryClient, user: UserResponse | undefined, locationId: number) => {
  if (!postingV2SwitchedOnNow(user?.id) || isOnsiteManager(user, locationId)) return false;
  const [optIn, postingPage] = await Promise.all([
    getFeatureFlag("agent-web-posting-opt-in", { queryClient }),
    getFeatureFlag("agent-web-posting", { queryClient }),
  ]);
  return postingV2Available({ optIn, postingPage });
};
